import { format } from 'date-fns';
import type {
  ActivityDraft,
  ActivityRule,
  ActivitySegment,
} from './activityAssistant.types';

export function durationMinutes(startedAt: string, endedAt: string): number {
  const milliseconds = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  return Math.max(1, Math.round(milliseconds / 60_000));
}

export function formatTaskDuration(minutes: number): string {
  const safeMinutes = Math.max(1, Math.round(minutes));
  const hours = Math.floor(safeMinutes / 60);
  const remainder = safeMinutes % 60;
  if (hours === 0) return `${remainder}m`;
  if (remainder === 0) return `${hours}h`;
  return `${hours}h ${remainder}m`;
}

export function taskDateForDraft(draft: ActivityDraft): string {
  return format(new Date(draft.startedAt), 'yyyy-MM-dd');
}

export function toActivityDraft(
  segment: ActivitySegment,
  rules: ActivityRule[] = [],
): ActivityDraft {
  const matchingRule = rules.find((rule) => {
    const processMatches = !rule.processName || rule.processName === segment.processName;
    const titleMatches =
      !rule.titleContains ||
      segment.windowTitle.toLocaleLowerCase().includes(rule.titleContains.toLocaleLowerCase());
    return processMatches && titleMatches;
  });

  return {
    id: segment.id,
    sourceSegmentIds: [segment.id],
    processName: segment.processName,
    applicationName: segment.applicationName || segment.processName,
    windowTitle: segment.windowTitle,
    title:
      matchingRule?.suggestedTitle ||
      segment.windowTitle ||
      segment.applicationName ||
      segment.processName,
    startedAt: segment.startedAt,
    endedAt: segment.endedAt,
    projectId: matchingRule?.projectId || '',
    memberIds: [],
    selected: !segment.ignored && !segment.importedTaskId,
    ignored: Boolean(segment.ignored),
    importedTaskId: segment.importedTaskId,
    suggestedByRuleId: matchingRule?.id,
  };
}

export function mergeActivityDrafts(drafts: ActivityDraft[]): ActivityDraft {
  if (drafts.length < 2) {
    throw new Error('Select at least two activities to merge');
  }

  const ordered = [...drafts].sort(
    (left, right) =>
      new Date(left.startedAt).getTime() - new Date(right.startedAt).getTime(),
  );
  const first = ordered[0];
  const last = ordered.reduce((latest, current) =>
    new Date(current.endedAt).getTime() > new Date(latest.endedAt).getTime()
      ? current
      : latest,
  );
  const applications = Array.from(
    new Set(ordered.map((draft) => draft.applicationName).filter(Boolean)),
  );

  return {
    ...first,
    id: `merged:${ordered.map((draft) => draft.id).join(':')}`,
    sourceSegmentIds: Array.from(
      new Set(ordered.flatMap((draft) => draft.sourceSegmentIds)),
    ),
    processName: applications.length === 1 ? first.processName : 'multiple',
    applicationName: applications.join(' + '),
    windowTitle: 'Combined activity',
    title: ordered.map((draft) => draft.title).filter(Boolean).join(' / '),
    startedAt: first.startedAt,
    endedAt: last.endedAt,
    projectId:
      ordered.every((draft) => draft.projectId === first.projectId)
        ? first.projectId
        : '',
    memberIds:
      ordered.every(
        (draft) => draft.memberIds.join(':') === first.memberIds.join(':'),
      )
        ? first.memberIds
        : [],
    importedTaskId: null,
    suggestedByRuleId: null,
  };
}

export function splitActivityDraft(draft: ActivityDraft): [ActivityDraft, ActivityDraft] {
  const startedAt = new Date(draft.startedAt).getTime();
  const endedAt = new Date(draft.endedAt).getTime();
  if (endedAt - startedAt < 120_000) {
    throw new Error('Activity must be at least two minutes long to split');
  }
  const midpoint = new Date(startedAt + Math.floor((endedAt - startedAt) / 2)).toISOString();

  return [
    {
      ...draft,
      id: `${draft.id}:part-1`,
      title: `${draft.title} (part 1)`,
      endedAt: midpoint,
      importedTaskId: null,
    },
    {
      ...draft,
      id: `${draft.id}:part-2`,
      title: `${draft.title} (part 2)`,
      startedAt: midpoint,
      importedTaskId: null,
    },
  ];
}

export function formatActivityTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
